"use client";

import React from "react";
import { BriefcaseBusiness, CircleCheck, FileText } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const teamMembers = [{
  name: "Jon Schiffer, CPA, CMA",
  role: "Executive Consultant & CEO",
  focus: "Financial management, funding accountability, audit readiness, reporting, and long-term planning.",
  bio: "Jon works with clients on financial management, funding accountability, audit readiness, reporting, and long-term planning. His work includes reviewing financial records, funding agreements, program activity, and reporting requirements to help clients understand their financial position, address complex issues, and make practical decisions. He also works with leadership, managers, funders, and auditors to improve how financial information is tracked, explained, and reported.",
  image: "https://riverbendcorp.ca/wp-content/uploads/2026/05/ChatGPT-Image-May-19-2026-10_51_01-AM.png?v=mr4rk0awv4"
}, {
  name: "Tish Hukezalie, CPA",
  role: "Financial Reporting and Program Support Lead",
  focus: "Financial reporting, audit readiness, budgets, funding accountability, and client training.",
  bio: "Tish supports First Nation communities and their related organizations with financial reporting, audit readiness, budgets, and funding accountability. Their work includes program management and reporting, strengthening internal financial controls, and training client finance staff to build financial understanding and confidence in their daily work. They bring a culturally informed approach to First Nations accounting, finance, and governance, with a focus on practical support that helps clients manage their responsibilities and build lasting capacity.",
  image: "https://riverbendcorp.ca/wp-content/uploads/2026/11/Smiling-illustrated-team-portrait.png"
}];
const teamStrengths = ["Practical financial leadership", "Project and reporting follow-through", "Respectful client support"];
function getInitials(name: string) {
  return name.split(" ").filter(part => !part.includes(",") && part !== "CPA" && part !== "CMA" && part.length > 0).slice(0, 2).map(part => part[0]).join("");
}
export default function OurFoundersVision() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="73026" id="our-leadership-team" className="bg-background text-foreground py-16 md:py-24 overflow-hidden relative">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(53,128,121,0.08),transparent_38%)] pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(17,47,75,0.05),transparent_42%)] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div ref={headerRef} className={`max-w-4xl mx-auto text-center mb-14 md:mb-16 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="text-secondary font-default font-bold tracking-widest uppercase text-sm mb-4">
            Our Team
          </p>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-default font-bold tracking-tight text-primary-500 leading-tight mb-6">
            Practical support from people who understand the work.
          </h2>

          <p className="max-w-3xl mx-auto text-lg md:text-xl leading-relaxed text-muted-foreground font-default">
            RiverBend is a small, experienced team supporting First Nation
            communities, Indigenous organizations, businesses, and partnerships
            with financial management, funding accountability, audit readiness,
            project support, reporting, and stronger internal systems.
          </p>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto text-left">
            {teamStrengths.map((item, index) => <div key={item} data-index={index} className="flex items-start gap-3">
                <CircleCheck className="size-5 text-secondary shrink-0 mt-0.5" />
                <span className="text-primary-700 font-default font-medium">
                  {item}
                </span>
              </div>)}
          </div>
        </div>

        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-2 gap-6 xl:gap-8 max-w-4xl mx-auto transition-all duration-1000 delay-200 ease-out ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {teamMembers.map((member, index) => <Card key={member.name} data-index={index} className="h-full bg-card border border-border shadow-sm hover:shadow-md hover:border-secondary/40 transition-all duration-300 rounded-lg overflow-hidden">
              <div className="relative aspect-[4/5] overflow-hidden bg-primary-100">
                <div className="absolute inset-0 flex items-center justify-center bg-primary-100">
                  <span className="text-primary-500/45 text-5xl font-default font-bold">
                    {getInitials(member.name)}
                  </span>
                </div>

                <img src={member.image} alt={`${member.name}, ${member.role}`} className="relative z-10 object-cover w-full h-full transition-transform duration-700 ease-in-out hover:scale-105" loading="lazy" />

                <div className="absolute inset-0 z-20 bg-primary/5 pointer-events-none" />
              </div>

              <CardContent className="p-6 md:p-7">
                <h3 className="text-xl md:text-[22px] font-default font-bold text-primary-500 mb-2 leading-tight whitespace-nowrap">
                  {member.name}
                </h3>

                <p className="text-secondary font-default font-bold tracking-widest uppercase text-[11px] mb-5 leading-relaxed min-h-[2.5rem]">
                  {member.role}
                </p>

                <div className="bg-secondary/10 border border-secondary/20 rounded-md p-4 mb-5 flex gap-3">
                  <BriefcaseBusiness className="size-5 text-secondary shrink-0 mt-0.5" />
                  <p className="text-sm text-muted-foreground font-default leading-relaxed">
                    {member.focus}
                  </p>
                </div>

                <p className="text-muted-foreground leading-relaxed font-default">
                  {member.bio}
                </p>
              </CardContent>
            </Card>)}
        </div>

        <div className="mt-12 rounded-2xl border border-border bg-secondary/5 p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="size-12 rounded-md bg-secondary/10 flex items-center justify-center shrink-0">
              <FileText className="size-6 text-secondary" />
            </div>

            <div>
              <h3 className="text-xl md:text-2xl font-default font-bold text-primary-500 mb-2">
                A small team with practical depth.
              </h3>

              <p className="text-muted-foreground font-default leading-relaxed max-w-3xl">
                RiverBend’s team works closely with clients to understand the
                real issues behind the numbers, organize information, support
                decisions, and help important work move forward.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}